import React from "react";
import { AlertTriangle, Download, RotateCcw, Trash2 } from "lucide-react";
import { SESSION_KEY } from "./srpText";

class SrpErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  exportDraft = () => {
    try {
      const raw = sessionStorage.getItem(this.props.sessionKey);
      const payload = raw
        ? { warning: "Raw SRP session export after an application error.", exportedAt: new Date().toISOString(), session: JSON.parse(raw) }
        : { warning: "No SRP session draft existed when the application error occurred.", exportedAt: new Date().toISOString() };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "srp-session-recovery.json";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      const blob = new Blob([sessionStorage.getItem(this.props.sessionKey) || ""], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "srp-session-recovery.txt";
      anchor.click();
      URL.revokeObjectURL(url);
    }
  };

  clearDraft = () => {
    if (!window.confirm("Sitzungsdaten wirklich löschen? Dieser Schritt kann nicht rückgängig gemacht werden.")) return;
    sessionStorage.removeItem(this.props.sessionKey);
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main data-testid="srp-error-boundary" className="min-h-screen bg-[#030712] px-6 py-28 text-white">
        <div role="alert" className="mx-auto max-w-2xl rounded-sm border border-[#F59E0B]/40 bg-[#FFF7ED] p-8 text-[#1F2937] shadow-2xl">
          <div className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-[#F59E0B]/15 text-[#B45309]">
            <AlertTriangle size={21} />
          </div>
          <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight">Der SRP-Arbeitsbereich konnte nicht geladen werden.</h1>
          <p className="mt-4 text-base leading-relaxed text-[#4B5563]">
            Es wurden keine Formulardaten übermittelt. Sie können die Seite erneut laden, die lokale Falldatei sichern oder die Sitzungsdaten löschen.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button type="button" data-testid="srp-error-reload" onClick={() => window.location.reload()} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-sm bg-[#0052FF] px-5 py-3 text-sm font-semibold text-white">
              <RotateCcw size={15} /> Erneut laden
            </button>
            <button type="button" data-testid="srp-error-export" onClick={this.exportDraft} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-sm border border-[#0052FF]/30 px-5 py-3 text-sm font-semibold text-[#0052FF]">
              <Download size={15} /> Sitzungsdaten exportieren
            </button>
            <button type="button" data-testid="srp-error-clear" onClick={this.clearDraft} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-sm border border-[#B91C1C]/30 px-5 py-3 text-sm font-semibold text-[#B91C1C]">
              <Trash2 size={15} /> Sitzungsdaten löschen
            </button>
          </div>
        </div>
      </main>
    );
  }
}

export const SrpErrorBoundary = ({ children }) => (
  <SrpErrorBoundaryInner sessionKey={SESSION_KEY}>{children}</SrpErrorBoundaryInner>
);
